import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { CreditCard, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";

// Bookings taken before taxes were itemised stored flat gst/pst/hst/qst
// amounts with no rates — their invoice is rebuilt from those so a past
// receipt keeps showing exactly what was charged.
const LEGACY_TAX_KEYS = [["gst", "GST"], ["hst", "HST"], ["pst", "PST"], ["qst", "QST"]];

function taxLines(tb) {
  if (Array.isArray(tb.lines) && tb.lines.length) {
    return tb.lines.map(l => ({
      key: l.code, name: l.name, rate: Number(l.rate) || 0, amount: Number(l.amount) || 0,
    }));
  }
  return LEGACY_TAX_KEYS
    .filter(([k]) => Number(tb[k] || 0) > 0)
    .map(([k, name]) => ({ key: k, name, rate: 0, amount: Number(tb[k]) }));
}

export default function BookingFlow() {
  const { id } = useParams();
  const nav = useNavigate();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(false);
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${id}`).then(r => setB(r.data)).catch(() => toast.error("Not found"));
  }, [id]);

  const pay = async () => {
    if (!consent) {
      toast.error("Please accept the Terms and Privacy Policy to continue");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        booking_id: id,
        origin_url: window.location.origin,
        terms_accepted: true,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment failed to start");
    }
    setLoading(false);
  };

  if (!b) return <div className="max-w-3xl mx-auto p-12 text-muted-foreground">Loading...</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="booking-flow">
      <Seo title="Confirm Booking – DriveTerrain" description="Confirm and pay for your driving lesson." canonical="/booking" robots="noindex, nofollow" hrefLangs={false} />
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-primary">Step 2 of 3 · Review & Pay</div>
      <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight mt-1">Confirm your booking</h1>

      <div className="mt-8 border border-border bg-card p-6 space-y-4">
        <Row k="Booking ID" v={b.booking_id} testid="bk-id" />
        <Row k="Type" v={b.booking_type === "package" ? "Package" : "Single session"} />
        <Row k="Hours" v={b.hours} />
        <Row k="Transmission" v={b.transmission} />
        {b.scheduled_at && <Row k="Scheduled" v={new Date(b.scheduled_at).toLocaleString()} />}
        {b.pickup_location && <Row k="Pickup" v={b.pickup_location} />}
        <div className="border-t border-border pt-3" />
        <Row k="Subtotal" v={`$${Number(b.amount_gross || 0).toFixed(2)}`} />
        {b.referral_discount > 0 && <Row k="Referral discount" v={`− $${Number(b.referral_discount).toFixed(2)}`} testid="referral-discount" />}
        {b.credits_applied > 0 && <Row k="Credit applied" v={`− $${Number(b.credits_applied).toFixed(2)}`} testid="credits-applied" />}
        {b.tax_breakdown && (
          <>
            {taxLines(b.tax_breakdown).map(line => (
              <Row
                key={line.key}
                k={line.rate ? `${line.name} ${line.rate}%` : line.name}
                v={`$${line.amount.toFixed(2)}`}
                testid={`tax-${line.key}`}
              />
            ))}
            {Number(b.tax_breakdown.total_tax || 0) > 0 && (
              <Row k="Total tax" v={`$${Number(b.tax_breakdown.total_tax).toFixed(2)}`} testid="tax-total" />
            )}
          </>
        )}
        <div className="border-t border-border pt-4 flex items-baseline justify-between">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Total due · CAD</div>
          <div className="font-display text-4xl font-semibold" data-testid="amount">${Number(b.amount).toFixed(2)}</div>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-3">
        {b.payment_status !== "paid" && Number(b.amount) > 0 && (
          <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="checkout-consent-label">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={consent}
              onChange={e => setConsent(e.target.checked)}
              data-testid="checkout-consent"
            />
            <span>
              I agree to DriveTerrain&apos;s{" "}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline text-primary" data-testid="checkout-terms-link">Terms of Service</a>
              {" "}and{" "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline text-primary" data-testid="checkout-privacy-link">Privacy Policy</a>.
              I authorize DriveTerrain to charge my card in Canadian dollars for this booking.
            </span>
          </label>
        )}
        <div className="flex justify-between">
          <Button variant="outline" className="rounded-sm" onClick={() => nav(-1)} data-testid="back-btn">Back</Button>
          {b.payment_status === "paid" ? (
            <Button className="rounded-sm" onClick={() => nav("/learner")} data-testid="goto-dashboard">Go to dashboard <ArrowRight className="ml-2 w-4 h-4" /></Button>
          ) : Number(b.amount) === 0 ? (
            <Button className="rounded-sm" onClick={() => nav("/learner")} data-testid="goto-dashboard">Confirm (no payment due) <ArrowRight className="ml-2 w-4 h-4" /></Button>
          ) : (
            <Button className="rounded-sm h-11 px-6" onClick={pay} disabled={loading || !consent} data-testid="pay-btn">
              <CreditCard className="mr-2 w-4 h-4" />
              {loading ? "Redirecting…" : "Pay with Stripe"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v, testid }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-medium" data-testid={testid}>{v}</span>
    </div>
  );
}
